import { MenuFoldOutlined, MenuUnfoldOutlined } from "@ant-design/icons";
import { Button } from "antd";
import { ReactElement, useEffect, useState } from "react";
import { Outlet, useLocation, useSearchParams } from "react-router-dom";
import { HeaderMain } from "../../components/HeaderMain/HeaderMain";
import { Logo } from "../../components/Logo/Logo";
import { SidebarMenu } from "../../components/SidebarMenu/SidebarMenu";
import { Echoi } from "../../services/websocket/connection";
import { useAppDispatch, useAppSelector } from "../../store/reduxHooks";
import { SELECT_NOTIFICATIONS_COUNT } from "../../store/selectors/notificationsSelectors";
import {
  UP_DATE_APPLICATION_STATUS,
  UP_DATE_IS_NEW_MESSAGE,
  UP_DATE_IS_READ,
} from "../../store/slice/applicationsSlice/applicationsSlice";
import { ID_USER_AUTH } from "../../store/slice/authSliceLogin/authSliceLogin";
import { ADD_APP_EVENT } from "../../store/slice/notificationsSlice/notificationsSlice";
import { REQUEST_CHAT_ADD_MESSAGE } from "../../store/slice/requestAppSlise/requestAppSlise";
import { IRouting } from "../../store/slice/routingSlice/routingSlice";
import { COPYRIGHT } from "../../constants/copyright";
import { PRIVACY_POLICY } from "../../constants/privacyPolicy";
import { resWssAppStatusType } from "../../types/resWssAppStatusType";
import { resWssAppBankType } from "../../types/resWssAppBankType";
import { resWssChatType } from "../../types/resWssChatType";

import classes from "./PageMainRoute.module.css";
import { useGetPermissionQuery } from "../../store/api/permission.api";

/**
 * Компонент основной страницы приложения.
 *
 * @param {Object} props - Свойства компонента.
 * @param {IRouting[]} props.routes - Массив маршрутов приложения.
 */
export function PageMainRoute(props: { routes: IRouting[] }): ReactElement {
  const { routes } = props;
  const date = new Date();
  const USER_ID = useAppSelector(ID_USER_AUTH);
  const countHistoryNotifications = useAppSelector(SELECT_NOTIFICATIONS_COUNT);
  const { data: permissionData } = useGetPermissionQuery(undefined);
  const [activeMenu, setActiveMenu] = useState("");
  const [openNotification, setOpenNotification] = useState(false);
  const [countNotification, setCountNotification] = useState(0);
  const [collapsed, setCollapsed] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();

  // установить количество уведомлений
  useEffect(() => {
    setCountNotification(countHistoryNotifications);
  }, [countHistoryNotifications, setCountNotification]);

  // задержка, чтобы сначала прогрузилась меню и сайдбар
  useEffect(() => {
    const delay = 30;

    const timer = setTimeout(() => {
      setShowContent(true);
    }, delay);

    return () => clearTimeout(timer);
  }, []);

  // подключение к сокету
  useEffect(() => {
    const WSS_ECHO = new Echoi();
    let isConnected = true;
    let connectionAttempts = 0;

    function connectWss() {
      const WSS_SISTEM = WSS_ECHO._getWss();

      if (isConnected) {
        if (USER_ID) {
          WSS_SISTEM.private("user." + USER_ID)
            .listen("PrivateUserMessageEvent", (res: resWssChatType) => {
              const app_id = Number(window.location.pathname.split("/").pop());
              if (res?.data?.message) {
                dispatch(
                  ADD_APP_EVENT({
                    app_id: res.data.message.application_id,
                    isNewMessage: true,
                  }),
                );
                dispatch(
                  UP_DATE_IS_NEW_MESSAGE({
                    id: +res.data.message.application_id,
                    isNewMessage: true,
                  }),
                );

                if (+res.data.message.application_id === app_id) {
                  dispatch(REQUEST_CHAT_ADD_MESSAGE(res?.data?.message));
                } else {
                  dispatch(
                    UP_DATE_IS_READ({
                      id: +res.data.message.application_id,
                      read: true,
                    }),
                  );
                }
              }
            })
            .listen("PrivateUserAppStatusEvent", (res: resWssAppStatusType) => {
              if (res.data.status) {
                dispatch(
                  ADD_APP_EVENT({
                    app_id: res.data.application.id,
                    application: { status: res.data.status },
                  }),
                );
                dispatch(
                  UP_DATE_IS_READ({
                    id: res.data.application.id,
                    read: true,
                  }),
                );
                dispatch(
                  UP_DATE_APPLICATION_STATUS({
                    id: res.data.application.id,
                    status: res.data.status,
                  }),
                );
              }
            })
            .listen(
              "PrivateUserAppBankChangeEvent",
              (res: resWssAppBankType) => {
                if (res.data.bank) {
                  dispatch(
                    ADD_APP_EVENT({
                      app_id: res.data.application.id,
                      bank: { title: res.data.bank.title },
                    }),
                  );
                  dispatch(
                    UP_DATE_IS_READ({
                      id: res.data.application.id,
                      read: true,
                    }),
                  );
                }
              },
            )
            .error((err: { type: string }) => {
              if (err.type === "AuthError" && connectionAttempts === 0) {
                connectionAttempts++;
                connectWss();
              } else {
                console.error("Уведомления перестали работать.");
                isConnected = false;
                WSS_SISTEM.disconnect();
              }
            });
        }
      }
      return () => {
        isConnected = false;
        WSS_SISTEM.disconnect();
      };
    }

    connectWss();
  }, [USER_ID, dispatch]);

  function onClickMenu() {
    setActiveMenu("menu");
  }

  function openBellOut() {
    setActiveMenu("bellOut");
    setOpenNotification(true);
  }

  function openUserPage() {
    setActiveMenu("user");
  }

  function closeNotificatrions() {
    setTimeout(() => setOpenNotification(false), 300);
  }

  function resetActiveMenu() {
    setActiveMenu("");
  }

  const transformSelectedKeys = () => {
    if (location.pathname !== "/") {
      return [location.pathname];
    } else {
      const status_preset = searchParams.get("status_preset");

      return [status_preset ? status_preset : location.pathname];
    }
  };

  const toggleIcon = collapsed ? (
    <MenuUnfoldOutlined className={classes.siderToggleIcon} />
  ) : (
    <MenuFoldOutlined className={classes.siderToggleIcon} />
  );

  return (
    <div className={classes.pageContainer}>
      <HeaderMain
        className={classes.header}
        activeMenu={activeMenu}
        openNotification={openNotification}
        countNotification={countNotification}
        onClickMenu={onClickMenu}
        openUserPage={openUserPage}
        openBellOut={openBellOut}
        closeNotificatrions={closeNotificatrions}
        resetActiveMenu={resetActiveMenu}
      />

      <aside
        className={`${classes.sider}${collapsed ? ` ${classes.siderCollapsed}` : ""}`}
      >
        <div className={classes.siderHeader}>
          <Logo to="/" isMarkOnly={collapsed} />
          <Button
            className={classes.siderToggleButton}
            type="text"
            onClick={() => setCollapsed((prev) => !prev)}
          >
            {toggleIcon}
          </Button>
        </div>
        <div className={classes.siderContent}>
          <SidebarMenu
            routes={routes}
            onClick={onClickMenu}
            selectedKeys={transformSelectedKeys()}
            collapsed={collapsed}
          />
        </div>

        <div className={classes.copyright}>
          <div className={classes.privacyPolicy}>
            <a
              href={"/docs/privacyPolicy.docx"}
              download
              target="_blank"
              rel="noopener noreferrer"
              className={classes.privacyPolicyLink}
            >
              {PRIVACY_POLICY}
            </a>
          </div>
          {COPYRIGHT}, {date?.getFullYear()}.
        </div>
      </aside>

      <main className={classes.mainContainer}>{showContent && <Outlet />}</main>
    </div>
  );
}
